import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { transactionsService } from "../../services/transactionsService";
import {
  TransactionFiltersDTO,
  TransactionsDto,
  WithdrawalDTO,
} from "../../models/Transaction";
import { UserService } from "../../services/userService";

export interface CustomerTransactionsState {
  transactions: TransactionsDto[];
  withdrawals:WithdrawalDTO[];
  withdrawalsAdmin:WithdrawalDTO[];
  errors: string | null;
  loading: boolean;
}

const initialState: CustomerTransactionsState = {
  transactions: [],
  withdrawals:[],
  withdrawalsAdmin:[],
  errors: null,
  loading: false,
};

// Thunk to fetch all customer transactions
export const getAllCustomerTransactions = createAsyncThunk<
  TransactionsDto[],
  { customerId: string; filters: TransactionFiltersDTO }
>(
  "customerTransactions/getAll",
  async ({ customerId, filters }, { rejectWithValue }) => {
    try {
      const res = await transactionsService.getAllTransactionsByUser(
        customerId,
        filters
      );
      return res.data;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to fetch transactions";
      return rejectWithValue(errorMessage);
    }
  }
);
export const getAllWithdrawForCustomer = createAsyncThunk<
  WithdrawalDTO[],
  { filters: TransactionFiltersDTO }
>(
  "withdrawals/all",
  async ({ filters }, { rejectWithValue }) => {
    try {
      const res = await UserService.getAllWithdrawForCustomer(
        filters
      );
      return res.data;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to fetch withdrawals";
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAllWithdrawals = createAsyncThunk<
  WithdrawalDTO[],
  { filters: TransactionFiltersDTO }
>(
  "withdrawals/admin/all",
  async ({ filters }, { rejectWithValue }) => {
    try {
      const res = await UserService.getAllWithdrawals(
        filters
      );
      return res.data;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to fetch withdrawals";
      return rejectWithValue(errorMessage);
    }
  }
);

export const manageWithdrawal = createAsyncThunk<
  any,
  { action: 'approve' | 'reject'; withdrawId: string; adminNotes: string }
>(
  "withdrawals/manage",
  async ({ action, withdrawId, adminNotes }, { rejectWithValue }) => {
    try {
      const res = await UserService.manageWithdrawal(action, {
        withdrawId,
        adminNotes,
      });
      return res; 
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error.message ||
        "Failed to manage withdrawal";
      return rejectWithValue(errorMessage);
    }
  }
);


const customerTransactionsSlice = createSlice({
  name: "customerTransactions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllCustomerTransactions.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(getAllCustomerTransactions.fulfilled, (state, action) => {
        state.transactions = action.payload; // Update with fetched transactions
        state.loading = false;
        state.errors = null;
      })
      .addCase(getAllCustomerTransactions.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload as string;
      })
      .addCase(getAllWithdrawForCustomer.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(getAllWithdrawForCustomer.fulfilled, (state, action) => {
        state.withdrawals = action.payload; // Update with fetched transactions
        state.loading = false;
        state.errors = null;
      })
      .addCase(getAllWithdrawForCustomer.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload as string;
      })
      .addCase(getAllWithdrawals.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(getAllWithdrawals.fulfilled, (state, action) => {
        state.withdrawalsAdmin = action.payload; // Update with fetched transactions
        state.loading = false;
        state.errors = null;
      })
      .addCase(getAllWithdrawals.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload as string;
      })
      .addCase(manageWithdrawal.pending, (state) => {
      state.loading = true;
      state.errors = null;
      })
      .addCase(manageWithdrawal.fulfilled, (state, action) => {
      state.loading = false;
      state.errors = null;
      state.withdrawalsAdmin = action.payload; 

      })
      .addCase(manageWithdrawal.rejected, (state, action) => {
      state.loading = false;
      state.errors = action.payload as string;
      });

  },
});

export default customerTransactionsSlice.reducer;
